import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApiIcon from '@mui/icons-material/Api';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/** Purpose: Build empty api form within the api management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const emptyApiForm = () => ({
  apiId: '',
  name: '',
  description: '',
  method: 'GET',
  url: '',
  headers: {},
  query: {},
  body: {},
  auth: { type: 'none' },
  timeoutMs: 15000,
  bodyType: 'json',
  retryOnFailure: false,
  retryCount: 1,
  followRedirects: true,
  strictSSL: true,
  encoding: 'utf8',
  defaultMessage: '',
  importSource: 'manual',
  rawCurl: '',
  rawJson: '',
});

/** Purpose: Resolve safe json parse for the api management module. Why: The surrounding operation uses this value to make its decision. */
const safeJsonParse = (value, fallback = {}) => {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    return fallback;
  }
};

/** Purpose: Transform object to pairs within the api management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const objectToPairs = (value = {}) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return [{ key: '', value: '' }];
  }

  /** Purpose: Build entries within the api management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const entries = Object.entries(value).map(([key, itemValue]) => ({
    key,
    value: itemValue == null ? '' : typeof itemValue === 'string' ? itemValue : JSON.stringify(itemValue),
  }));

  return entries.length > 0 ? entries : [{ key: '', value: '' }];
};

/** Purpose: Aggregate pairs to object within the api management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
const pairsToObject = (items = []) => items.reduce((accumulator, item) => {
  const key = String(item?.key || '').trim();
  if (!key) {
    return accumulator;
  }
  accumulator[key] = String(item?.value ?? '');
  return accumulator;
}, {});

/** Purpose: Perform extract api placeholders for the api management module. Why: Callers need a consistent result from this shared operation. */
const extractApiPlaceholders = (value, found = []) => {
  if (typeof value === 'string') {
    const pattern = /\{\{\{?\s*([a-zA-Z0-9_.-]+)\s*\}\}\}?/g;
    let match;
    while ((match = pattern.exec(value)) !== null) {
      const name = String(match[1] || '').trim();
      if (name && !found.includes(name)) found.push(name);
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => extractApiPlaceholders(item, found));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => extractApiPlaceholders(item, found));
  }
  return found;
};

/** Purpose: Perform parse curl for the api management module. Why: Callers need a consistent result from this shared operation. */
const parseCurl = (curlText = '') => {
  const text = String(curlText || '').trim();
  const methodMatch = text.match(/(?:^|\s)-X\s+([A-Z]+)/i);
  const urlMatch = text.match(/curl\s+(?:-X\s+[A-Z]+\s+)?["']?([^"'\s]+)["']?/i);
  const headerMatches = [...text.matchAll(/(?:^|\s)-H\s+["']([^"']+)["']/gi)];
  const dataMatch = text.match(/(?:^|\s)--data(?:-raw)?\s+["']([\s\S]*?)["'](?=\s+-|$)/i);
  const headers = {};

  headerMatches.forEach((match) => {
    const [key, ...rest] = String(match[1] || '').split(':');
    if (!key) return;
    headers[key.trim()] = rest.join(':').trim();
  });

  let body = {};
  const rawBody = dataMatch?.[1] || '';
  if (rawBody) {
    try {
      body = JSON.parse(rawBody);
    } catch (error) {
      body = { raw: rawBody };
    }
  }

  return {
    name: '',
    description: '',
    method: methodMatch?.[1]?.toUpperCase() || (rawBody ? 'POST' : 'GET'),
    url: urlMatch?.[1] || '',
    headers,
    query: {},
    body,
    auth: { type: 'none' },
    timeoutMs: 15000,
    importSource: 'curl',
    rawCurl: text,
    rawJson: '',
  };
};

/** Purpose: Perform parse json definition for the api management module. Why: Callers need a consistent result from this shared operation. */
const parseJsonDefinition = (jsonText = '') => {
  const parsed = JSON.parse(jsonText || '{}');
  const request = parsed.request || parsed;
  return {
    name: parsed.name || request.name || '',
    description: parsed.description || request.description || '',
    method: String(request.method || 'GET').toUpperCase(),
    url: typeof request.url === 'string'
      ? request.url
      : request.url?.raw || '',
    headers: request.headers || {},
    query: request.query || {},
    body: request.body || {},
    auth: request.auth || { type: 'none' },
    timeoutMs: Number(request.timeoutMs) || 15000,
    importSource: 'json',
    rawCurl: '',
    rawJson: jsonText,
  };
};

/** Purpose: Render the json field component. Why: This component provides the api management view used by its callers. */
function JsonField({ label, value, onChange, minRows = 5, placeholder = '{}', helperText = '' }) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      multiline
      minRows={minRows}
      fullWidth
      placeholder={placeholder}
      helperText={helperText}
      InputProps={{
        sx: {
          fontFamily: 'monospace',
          fontSize: 13,
          color: 'text.primary',
          '& textarea': {
            color: 'text.primary',
            WebkitTextFillColor: 'currentColor',
            caretColor: 'currentColor',
          },
        },
      }}
      InputLabelProps={{
        sx: {
          color: 'text.secondary',
        },
      }}
      FormHelperTextProps={{
        sx: {
          mx: 0,
        },
      }}
    />
  );
}

/** Purpose: Render the key value editor component. Why: This component provides the api management view used by its callers. */
function KeyValueEditor({ label, items = [], onChange, valueLabel = 'Value', helpText = '' }) {
  /** Purpose: Apply update item in the api management module. Why: Centralizing this change keeps the relevant state or persisted record consistent. */
  const updateItem = (index, patch) => {
    onChange(items.map((item, currentIndex) => (
      currentIndex === index ? { ...item, ...patch } : item
    )));
  };

  /** Purpose: Handle add item in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const addItem = () => onChange([...(items.length > 0 ? items : []), { key: '', value: '' }]);
  /** Purpose: Handle remove item in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const removeItem = (index) => onChange(items.filter((_, currentIndex) => currentIndex !== index));

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {label}
      </Typography>
      {helpText ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          {helpText}
        </Typography>
      ) : null}
      <Stack spacing={1.25}>
        {(items.length > 0 ? items : [{ key: '', value: '' }]).map((item, index) => (
          <Stack key={`${label}-${index}`} direction={{ xs: 'column', md: 'row' }} spacing={1.25}>
            <TextField
              label="Key"
              value={item.key || ''}
              onChange={(event) => updateItem(index, { key: event.target.value })}
              fullWidth
            />
            <TextField
              label={valueLabel}
              value={item.value || ''}
              onChange={(event) => updateItem(index, { value: event.target.value })}
              fullWidth
            />
            <Button color="error" variant="outlined" onClick={() => removeItem(index)} sx={{ minWidth: { md: 96 } }}>
              Remove
            </Button>
          </Stack>
        ))}
        <Button variant="outlined" onClick={addItem} sx={{ alignSelf: 'flex-start' }}>
          Add row
        </Button>
      </Stack>
    </Box>
  );
}

/** Purpose: configure reusable API connections and credentials. Why: flow nodes need centrally managed integrations rather than duplicated request setup. */
export default function ApiManagement() {
  const { selectedBot, selectedEnv, selectedBotDetails } = useAuth();
  const isReadOnly = selectedEnv === 'prod';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [environmentId, setEnvironmentId] = useState('');
  const [apis, setApis] = useState([]);
  const [activeApiId, setActiveApiId] = useState('');
  const [importTab, setImportTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [apiForm, setApiForm] = useState(emptyApiForm());
  const [headerRows, setHeaderRows] = useState(objectToPairs({}));
  const [queryRows, setQueryRows] = useState(objectToPairs({}));
  const [bodyText, setBodyText] = useState('{}');
  const [bodyError, setBodyError] = useState('');
  const [testingApiId, setTestingApiId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testApiTarget, setTestApiTarget] = useState(null);
  const [testVariableValues, setTestVariableValues] = useState({});
  const [responseTab, setResponseTab] = useState(0);
  const [pageTab, setPageTab] = useState(0);
  const [requestTab, setRequestTab] = useState(0);
  const [search, setSearch] = useState('');
  const [usageDays, setUsageDays] = useState(7);
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);

  /** Purpose: Handle selected api in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const selectedApi = useMemo(
    () => apis.find((item) => item.apiId === activeApiId) || null,
    [apis, activeApiId]
  );
  /** Purpose: Filter visible apis within the api management module. Why: This keeps the surrounding operation consistent with the current interface state and data shape. */
  const visibleApis = useMemo(() => apis.filter((api) => `${api.name} ${api.url}`.toLowerCase().includes(search.toLowerCase())), [apis, search]);
  /** Purpose: Resolve api placeholders for the api management module. Why: The surrounding operation uses this value to make its decision. */
  const apiPlaceholders = useMemo(() => extractApiPlaceholders({
    url: apiForm.url,
    headers: pairsToObject(headerRows),
    query: pairsToObject(queryRows),
    body: bodyText,
    auth: apiForm.auth,
  }), [apiForm.url, apiForm.auth, headerRows, queryRows, bodyText]);

  /** Purpose: Load load environment id for the api management module. Why: The selected view needs current data to render or continue processing. */
  const loadEnvironmentId = async () => {
    const response = await axios.get(`/api/v1/environments/${selectedBot}/${selectedEnv}`);
    return response.data?.environment?.envId || '';
  };

  /** Purpose: Load load apis for the api management module. Why: The selected view needs current data to render or continue processing. */
  const loadApis = async (resolvedEnvironmentId) => {
    const response = await axios.get(`/api/v1/api-definitions?botId=${selectedBot}&environmentId=${resolvedEnvironmentId}`);
    const nextApis = Array.isArray(response.data?.apis) ? response.data.apis : [];
    setApis(nextApis);
    setActiveApiId((current) => current || nextApis[0]?.apiId || '');
  };

  useEffect(() => {
    /** Purpose: Load bootstrap for the api management module. Why: The selected view needs current data to render or continue processing. */
    const bootstrap = async () => {
      if (!selectedBot || !selectedEnv) {
        setLoading(false);
        setMessage({ type: 'info', text: 'Select a bot and environment to manage APIs.' });
        setApis([]);
        return;
      }

      setLoading(true);
      try {
        const resolvedEnvironmentId = await loadEnvironmentId();
        setEnvironmentId(resolvedEnvironmentId);
        await loadApis(resolvedEnvironmentId);
        setMessage(null);
      } catch (error) {
        setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load API management.' });
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [selectedBot, selectedEnv]);

  /** Purpose: Handle open create dialog in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openCreateDialog = (mode = 'manual') => {
    setImportTab(mode === 'curl' ? 1 : mode === 'json' ? 2 : 0);
    const nextForm = {
      ...emptyApiForm(),
      importSource: mode,
    };
    setApiForm(nextForm);
    setHeaderRows(objectToPairs(nextForm.headers));
    setQueryRows(objectToPairs(nextForm.query));
    setBodyText(typeof nextForm.body === 'string' ? nextForm.body : JSON.stringify(nextForm.body || {}, null, 2));
    setBodyError('');
    setDialogOpen(true);
  };

  /** Purpose: Handle open edit dialog in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const openEditDialog = (api) => {
    if (!api) return;
    setImportTab(api.importSource === 'curl' ? 1 : api.importSource === 'json' ? 2 : 0);
    const nextForm = {
      apiId: api.apiId,
      name: api.name || '',
      description: api.description || '',
      method: api.method || 'GET',
      url: api.url || '',
      headers: api.headers || {},
      query: api.query || {},
      body: api.body || {},
      auth: api.auth || { type: 'none' },
      timeoutMs: Number(api.timeoutMs) || 15000,
      bodyType: api.bodyType || 'json',
      retryOnFailure: Boolean(api.retryOnFailure),
      retryCount: Number(api.retryCount) || 1,
      followRedirects: api.followRedirects !== false,
      strictSSL: api.strictSSL !== false,
      encoding: api.encoding || 'utf8',
      defaultMessage: api.defaultMessage || '',
      importSource: api.importSource || 'manual',
      rawCurl: api.rawCurl || '',
      rawJson: api.rawJson || '',
    };
    setApiForm(nextForm);
    setHeaderRows(objectToPairs(nextForm.headers));
    setQueryRows(objectToPairs(nextForm.query));
    setBodyText(typeof nextForm.body === 'string' ? nextForm.body : JSON.stringify(nextForm.body || {}, null, 2));
    setBodyError('');
    setDialogOpen(true);
  };

  /** Purpose: Handle handle import curl in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleImportCurl = () => {
    const parsed = parseCurl(apiForm.rawCurl || '');
    setApiForm((current) => {
      const nextForm = {
      ...current,
      ...parsed,
      name: current.name || parsed.name,
      description: current.description || parsed.description,
      };
      setHeaderRows(objectToPairs(nextForm.headers));
      setQueryRows(objectToPairs(nextForm.query));
      setBodyText(JSON.stringify(nextForm.body || {}, null, 2));
      setBodyError('');
      return nextForm;
    });
  };

  /** Purpose: Handle handle import json in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleImportJson = () => {
    try {
      const parsed = parseJsonDefinition(apiForm.rawJson || '');
      setApiForm((current) => {
        const nextForm = {
        ...current,
        ...parsed,
        name: current.name || parsed.name,
        description: current.description || parsed.description,
        };
        setHeaderRows(objectToPairs(nextForm.headers));
        setQueryRows(objectToPairs(nextForm.query));
        setBodyText(JSON.stringify(nextForm.body || {}, null, 2));
        setBodyError('');
        return nextForm;
      });
      setMessage(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Invalid JSON format for API import.' });
    }
  };

  /** Purpose: Handle save api in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const saveApi = async () => {
    if (!selectedBot || !environmentId || !apiForm.name.trim() || !apiForm.url.trim()) return;
    let parsedBody = bodyText;
    if (['json', 'graphql', 'form_urlencoded', 'form_data'].includes(apiForm.bodyType || 'json')) {
      try {
        parsedBody = safeJsonParse(bodyText || '{}', {});
        JSON.parse(bodyText || '{}');
        setBodyError('');
      } catch (error) {
        setBodyError('Enter valid JSON for this body type before saving.');
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        botId: selectedBot,
        environmentId,
        ...apiForm,
        headers: pairsToObject(headerRows),
        query: pairsToObject(queryRows),
        body: parsedBody,
      };

      if (apiForm.apiId) {
        await axios.put(`/api/v1/api-definitions/${apiForm.apiId}`, payload);
      } else {
        await axios.post('/api/v1/api-definitions', payload);
      }

      await loadApis(environmentId);
      setDialogOpen(false);
      setMessage({ type: 'success', text: 'API definition saved successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to save API definition.' });
    } finally {
      setSaving(false);
    }
  };

  /** Purpose: Handle delete api in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const deleteApi = async (api) => {
    if (!api || !window.confirm(`Delete API "${api.name}"?`)) return;
    try {
      await axios.delete(`/api/v1/api-definitions/${api.apiId}`);
      await loadApis(environmentId);
      setMessage({ type: 'success', text: 'API definition deleted successfully.' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to delete API definition.' });
    }
  };

  /** Purpose: Resolve execute test for the api management module. Why: The surrounding operation uses this value to make its decision. */
  const executeTest = async (api, variables = {}) => {
    if (!api || testingApiId) return;
    setTestingApiId(api.apiId);
    setTestResult(null);
    setResponseTab(0);
    try {
      const response = await axios.post(`/api/v1/api-definitions/${api.apiId}/test`, { variables });
      setTestResult({ ...response.data, apiId: api.apiId });
    } catch (error) {
      setTestResult({
        apiId: api.apiId,
        success: false,
        error: error.response?.data?.error || 'API test failed.',
      });
    } finally {
      setTestingApiId('');
    }
  };

  /** Purpose: Perform test api for the api management module. Why: Callers need a consistent result from this shared operation. */
  const testApi = (api) => {
    if (!api || testingApiId) return;
    const placeholders = extractApiPlaceholders({
      url: api.url,
      headers: api.headers,
      query: api.query,
      body: api.body,
      auth: api.auth,
    });
    if (placeholders.length === 0) {
      executeTest(api);
      return;
    }
    setTestApiTarget(api);
    setTestVariableValues(placeholders.reduce((values, placeholder) => ({ ...values, [placeholder]: '' }), {}));
  };

  /** Purpose: Load load usage for the api management module. Why: The selected view needs current data to render or continue processing. */
  const loadUsage = async (apiId = activeApiId, days = usageDays) => {
    if (!apiId) return;
    setUsageLoading(true);
    try {
      const response = await axios.get(`/api/v1/api-definitions/${apiId}/usage?days=${days}`);
      setUsage(response.data?.usage || null);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to load API usage.' });
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    if (pageTab === 1 && activeApiId) loadUsage(activeApiId, usageDays);
  }, [pageTab, activeApiId, usageDays]);

  const responseBodyText = testResult?.data === undefined
    ? ''
    : typeof testResult.data === 'string'
      ? testResult.data
      : JSON.stringify(testResult.data, null, 2);

  /** Purpose: Handle copy response in the api management module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const copyResponse = async () => {
    const value = responseTab === 0
      ? responseBodyText
      : JSON.stringify(testResult?.headers || {}, null, 2);
    await navigator.clipboard.writeText(value);
    setMessage({ type: 'success', text: 'Response copied to clipboard.' });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: '60vh' }} aria-busy={loading}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.03em' }}>
            API Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import APIs from cURL or JSON, add them manually, and view every API integrated for {selectedBotDetails?.botName || selectedBot} in {selectedEnv}.
          </Typography>
        </Box>
        {!isReadOnly && <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="outlined" onClick={() => openCreateDialog('curl')}>Import cURL</Button>
          <Button variant="outlined" onClick={() => openCreateDialog('json')}>Import JSON</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog('manual')}>Add API manually</Button>
        </Stack>}
      </Box>

      {loading ? <LinearProgress aria-label="Loading API management" sx={{ borderRadius: 1 }} /> : null}

      {message ? (
        <Alert severity={message.type} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      ) : null}
      {isReadOnly ? <Alert severity="info">PROD APIs are view-only. Edit them in DEV and deploy again.</Alert> : null}

      <Tabs value={pageTab} onChange={(_, value) => setPageTab(value)}>
        <Tab label="Manage" />
        <Tab label="Usage" />
      </Tabs>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
        <Paper sx={{ p: 1.5, borderRadius: 1, position: { md: 'sticky' }, top: { md: 16 }, maxHeight: { md: 'calc(100vh - 140px)' }, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
            Saved APIs
          </Typography>
          <TextField size="small" placeholder="Search APIs" value={search} onChange={(event) => setSearch(event.target.value)} fullWidth sx={{ mb: 1.25 }} />
          <Stack spacing={0.75}>
            {loading ? (
              <Typography variant="body2" color="text.secondary" role="status">Loading saved APIs…</Typography>
            ) : visibleApis.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No APIs added yet. Import one from cURL, JSON, or create it manually.
              </Typography>
            ) : visibleApis.map((api) => (
              <Paper
                key={api.apiId}
                onClick={() => setActiveApiId(api.apiId)}
                sx={{
                  p: 1.25,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: api.apiId === activeApiId ? '1px solid' : '1px solid transparent',
                  borderColor: api.apiId === activeApiId ? 'primary.main' : 'divider',
                  bgcolor: api.apiId === activeApiId ? 'rgba(37, 99, 235, 0.08)' : 'background.paper',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={700}>{api.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {api.url}
                    </Typography>
                  </Box>
                  <Chip size="small" label={api.method} color="primary" variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap' }} useFlexGap>
                  <Typography variant="caption" color="text.secondary">{api.importSource || 'manual'}</Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">{new Date(api.updatedAt || api.createdAt || Date.now()).toLocaleDateString()}</Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, lg: 2 }, borderRadius: 1, minWidth: 0 }}>
          {loading ? (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight={320}>
              <Typography variant="body2" color="text.secondary" role="status">Loading API workspace…</Typography>
            </Box>
          ) : !selectedApi ? (
            <Typography variant="body2" color="text.secondary">
              Select an API to view its details.
            </Typography>
          ) : pageTab === 1 ? (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
                <Typography variant="h6" fontWeight={800}>{selectedApi.name} usage</Typography>
                <TextField select size="small" value={usageDays} onChange={(event) => setUsageDays(Number(event.target.value))} sx={{ width: 140 }}>
                  <MenuItem value={7}>Last 7 days</MenuItem><MenuItem value={30}>Last 30 days</MenuItem><MenuItem value={90}>Last 90 days</MenuItem>
                </TextField>
              </Stack>
              {usageLoading ? <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box> : (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 2 }}>
                    {[['Total requests', usage?.total || 0], ['Errors', usage?.errors || 0], ['Error rate', `${usage?.errorRate || 0}%`], ['Latency p50', `${usage?.latency?.p50 || 0} ms`]].map(([label, value]) => (
                      <Paper key={label} variant="outlined" sx={{ p: 2, borderRadius: 2 }}><Typography variant="body2" color="text.secondary">{label}</Typography><Typography variant="h5" fontWeight={800} sx={{ mt: 1 }}>{value}</Typography></Paper>
                    ))}
                  </Box>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>Response time</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2 }}>
                      {['min', 'p25', 'p50', 'p75', 'p99'].map((key) => <Box key={key}><Typography variant="caption" color="text.secondary">{key.toUpperCase()}</Typography><Typography variant="h6" fontWeight={700}>{usage?.latency?.[key] || 0} ms</Typography></Box>)}
                    </Box>
                  </Paper>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1 }}>Recent executions</Typography>
                    {(usage?.history || []).length === 0 ? <Typography color="text.secondary">No executions in this period.</Typography> : (usage.history.slice().reverse().slice(0, 20).map((item) => (
                      <Stack key={item.createdAt} direction="row" justifyContent="space-between" sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}><Typography variant="body2">{new Date(item.createdAt).toLocaleString()}</Typography><Stack direction="row" spacing={1}><Chip size="small" label={item.status || 'ERR'} color={item.success ? 'success' : 'error'} /><Chip size="small" label={`${item.durationMs} ms`} variant="outlined" /></Stack></Stack>
                    )))}
                  </Paper>
                </>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ApiIcon color="primary" />
                    <Typography variant="h6" fontWeight={800}>{selectedApi.name}</Typography>
                    <Chip size="small" label={selectedApi.method} color="primary" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {selectedApi.description || 'No description'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', wordBreak: 'break-all' }}>
                    {selectedApi.url}
                  </Typography>
                </Box>
                {!isReadOnly && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button
                    variant="contained"
                    startIcon={testingApiId === selectedApi.apiId ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                    onClick={() => testApi(selectedApi)}
                    disabled={Boolean(testingApiId)}
                  >
                    {testingApiId === selectedApi.apiId ? 'Testing...' : 'Test Run'}
                  </Button>
                  <Button variant="outlined" onClick={() => openEditDialog(selectedApi)}>Edit</Button>
                  <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={() => deleteApi(selectedApi)}>Delete</Button>
                </Stack>}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 0.9fr) minmax(360px, 1.1fr)' }, gap: 1.5, alignItems: 'start' }}>
                <Stack spacing={2} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`Timeout: ${(Number(selectedApi.timeoutMs) || 15000) / 1000}s`} variant="outlined" />
                  <Chip size="small" label={`Auth: ${selectedApi.auth?.type || 'none'}`} variant="outlined" />
                </Stack>
                <Paper variant="outlined" sx={{ borderRadius: 1, overflow: 'hidden' }}>
                <Tabs value={requestTab} onChange={(_, value) => setRequestTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tab label="Params" sx={{ minWidth: 72 }} /><Tab label="Headers" sx={{ minWidth: 78 }} /><Tab label="Body" sx={{ minWidth: 64 }} /><Tab label="Configuration" sx={{ minWidth: 112 }} />
                </Tabs>
                <Box sx={{ p: 2, minHeight: 330, bgcolor: 'background.default' }}>
                {requestTab === 1 ? <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Headers</Typography>
                  <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedApi.headers || {}, null, 2)}
                  </Typography>
                </Box> : null}
                {requestTab === 0 ? <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Query</Typography>
                  <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedApi.query || {}, null, 2)}
                  </Typography>
                </Box> : null}
                {requestTab === 2 ? <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Body</Typography>
                  <Chip size="small" label={selectedApi.bodyType || 'json'} sx={{ mb: 1 }} />
                  <Typography component="pre" variant="body2" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {JSON.stringify(selectedApi.body || {}, null, 2)}
                  </Typography>
                </Box> : null}
                {requestTab === 3 ? <Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <Typography variant="body2">Timeout: <b>{(Number(selectedApi.timeoutMs) || 15000) / 1000}s</b></Typography>
                    <Typography variant="body2">Retry: <b>{selectedApi.retryOnFailure ? `${selectedApi.retryCount || 1} times` : 'Off'}</b></Typography>
                    <Typography variant="body2">Redirects: <b>{selectedApi.followRedirects === false ? 'Off' : 'On'}</b></Typography>
                    <Typography variant="body2">Strict SSL: <b>{selectedApi.strictSSL === false ? 'Off' : 'On'}</b></Typography>
                    <Typography variant="body2">Encoding: <b>{selectedApi.encoding || 'utf8'}</b></Typography>
                    <Typography variant="body2">Auth: <b>{selectedApi.auth?.type || 'none'}</b></Typography>
                  </Box>
                  {selectedApi.defaultMessage ? <Alert severity="info" sx={{ mt: 2 }}>{selectedApi.defaultMessage}</Alert> : null}
                </Box> : null}
                </Box>
                </Paper>
                </Stack>

                <Paper sx={{ minWidth: 0, borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: testResult?.apiId === selectedApi.apiId ? (testResult.success ? 'success.main' : 'error.main') : 'divider', position: { lg: 'sticky' }, top: { lg: 16 } }}>
                  <Box sx={{ px: 2, pt: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Typography variant="subtitle2" fontWeight={800}>API Response</Typography>
                      {testResult?.apiId === selectedApi.apiId && !testResult.error ? (
                        <Tooltip title="Copy response">
                          <IconButton size="small" onClick={copyResponse}><ContentCopyIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                    {testResult?.apiId === selectedApi.apiId && !testResult.error ? (
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                        <Chip size="small" label={`${testResult.status} ${testResult.statusText || ''}`.trim()} color={testResult.success ? 'success' : 'error'} />
                        <Chip size="small" label={`${testResult.durationMs} ms`} variant="outlined" />
                        <Chip size="small" label={`${new Blob([responseBodyText]).size} bytes`} variant="outlined" />
                      </Stack>
                    ) : null}
                    <Tabs value={responseTab} onChange={(_, value) => setResponseTab(value)} sx={{ mt: 0.5, minHeight: 42 }}>
                      <Tab label="Body" sx={{ minHeight: 42 }} />
                      <Tab label="Headers" sx={{ minHeight: 42 }} />
                    </Tabs>
                  </Box>
                  <Box sx={{ minHeight: 380, maxHeight: 580, overflow: 'auto', bgcolor: '#10141d', color: '#d6deeb', p: 2 }}>
                    {testingApiId === selectedApi.apiId ? (
                      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 320 }}><CircularProgress size={28} /><Typography variant="body2" color="inherit">Sending request...</Typography></Stack>
                    ) : testResult?.apiId !== selectedApi.apiId ? (
                      <Typography variant="body2" sx={{ color: '#9aa7bd' }}>Run the API to view its response here.</Typography>
                    ) : testResult.error ? (
                      <Alert severity="error">{testResult.error}</Alert>
                    ) : (
                      <Typography component="pre" variant="body2" sx={{ m: 0, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, lineHeight: 1.7, color: 'inherit', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {responseTab === 0 ? (responseBodyText || '(empty response)') : JSON.stringify(testResult.headers || {}, null, 2)}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Box>
            </Stack>
          )}
        </Paper>
      </Box>

      <Dialog open={Boolean(testApiTarget)} onClose={() => !testingApiId && setTestApiTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Enter dynamic test values</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Provide values for this test only. Your saved API definition will not be changed.
          </Typography>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {Object.keys(testVariableValues).map((placeholder) => (
              <TextField
                key={placeholder}
                label={`{{${placeholder}}}`}
                value={testVariableValues[placeholder] || ''}
                onChange={(event) => setTestVariableValues((current) => ({ ...current, [placeholder]: event.target.value }))}
                fullWidth
                required
                autoFocus={Object.keys(testVariableValues)[0] === placeholder}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestApiTarget(null)} disabled={Boolean(testingApiId)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={testingApiId ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={Boolean(testingApiId) || Object.values(testVariableValues).some((value) => !String(value).trim())}
            onClick={async () => {
              const api = testApiTarget;
              setTestApiTarget(null);
              await executeTest(api, testVariableValues);
            }}
          >
            Run test
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{apiForm.apiId ? 'Edit API' : 'Add API'}</DialogTitle>
        <DialogContent sx={{ pt: '12px !important' }}>
          <Tabs value={importTab} onChange={(_, value) => setImportTab(value)} sx={{ mb: 2 }}>
            <Tab label="Manual" />
            <Tab label="Import cURL" />
            <Tab label="Import JSON" />
          </Tabs>

          <Stack spacing={2}>
            <Alert severity="info">
              Add dynamic API inputs anywhere using <code>{'{{variable_name}}'}</code> or <code>{'{{{variable_name}}}'}</code>. After selecting this API in a flow node, each placeholder will appear as a variable mapping field.
            </Alert>
            {importTab === 1 ? (
              <>
                <TextField
                  label="Paste cURL"
                  value={apiForm.rawCurl || ''}
                  onChange={(event) => setApiForm((current) => ({ ...current, rawCurl: event.target.value, importSource: 'curl' }))}
                  multiline
                  minRows={7}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleImportCurl}>Parse cURL</Button>
              </>
            ) : null}

            {importTab === 2 ? (
              <>
                <TextField
                  label="Paste JSON"
                  value={apiForm.rawJson || ''}
                  onChange={(event) => setApiForm((current) => ({ ...current, rawJson: event.target.value, importSource: 'json' }))}
                  multiline
                  minRows={7}
                  fullWidth
                />
                <Button variant="outlined" onClick={handleImportJson}>Parse JSON</Button>
              </>
            ) : null}

            <TextField
              label="API name"
              value={apiForm.name}
              onChange={(event) => setApiForm((current) => ({ ...current, name: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Description"
              value={apiForm.description}
              onChange={(event) => setApiForm((current) => ({ ...current, description: event.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select
                label="Method"
                value={apiForm.method}
                onChange={(event) => setApiForm((current) => ({ ...current, method: event.target.value }))}
                sx={{ minWidth: 160 }}
              >
                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => (
                  <MenuItem key={method} value={method}>{method}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="URL"
                value={apiForm.url}
                onChange={(event) => setApiForm((current) => ({ ...current, url: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Timeout (seconds)"
                type="number"
                value={(Number(apiForm.timeoutMs) || 15000) / 1000}
                onChange={(event) => setApiForm((current) => ({ ...current, timeoutMs: Math.min(60000, Math.max(1000, Number(event.target.value || 15) * 1000)) }))}
                inputProps={{ min: 1, max: 60 }}
                sx={{ width: { xs: '100%', md: 180 } }}
              />
            </Stack>
            {apiPlaceholders.length > 0 ? (
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Detected flow inputs:</Typography>
                {apiPlaceholders.map((placeholder) => <Chip key={placeholder} size="small" label={`{{${placeholder}}}`} color="primary" variant="outlined" />)}
              </Stack>
            ) : null}
            <TextField
              select
              label="Authentication"
              value={apiForm.auth?.type || 'none'}
              onChange={(event) => setApiForm((current) => ({ ...current, auth: { type: event.target.value } }))}
              sx={{ maxWidth: 260 }}
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="bearer">Bearer token</MenuItem>
              <MenuItem value="api_key">API key</MenuItem>
              <MenuItem value="basic">Basic auth</MenuItem>
            </TextField>
            {apiForm.auth?.type === 'bearer' ? <TextField label="Bearer token" type="password" value={apiForm.auth.token || ''} onChange={(event) => setApiForm((current) => ({ ...current, auth: { ...current.auth, token: event.target.value } }))} fullWidth /> : null}
            {apiForm.auth?.type === 'api_key' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Header name" value={apiForm.auth.headerName || ''} onChange={(event) => setApiForm((current) => ({ ...current, auth: { ...current.auth, headerName: event.target.value } }))} fullWidth />
                <TextField label="API key" type="password" value={apiForm.auth.apiKey || ''} onChange={(event) => setApiForm((current) => ({ ...current, auth: { ...current.auth, apiKey: event.target.value } }))} fullWidth />
              </Stack>
            ) : null}
            {apiForm.auth?.type === 'basic' ? (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Username" value={apiForm.auth.username || ''} onChange={(event) => setApiForm((current) => ({ ...current, auth: { ...current.auth, username: event.target.value } }))} fullWidth />
                <TextField label="Password" type="password" value={apiForm.auth.password || ''} onChange={(event) => setApiForm((current) => ({ ...current, auth: { ...current.auth, password: event.target.value } }))} fullWidth />
              </Stack>
            ) : null}
            <Typography variant="subtitle1" fontWeight={800}>Request configuration</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField select label="Body type" value={apiForm.bodyType || 'json'} onChange={(event) => setApiForm((current) => ({ ...current, bodyType: event.target.value }))}>
                <MenuItem value="json">JSON</MenuItem><MenuItem value="raw">Raw text</MenuItem><MenuItem value="xml">XML</MenuItem><MenuItem value="graphql">GraphQL</MenuItem><MenuItem value="form_urlencoded">x-www-form-urlencoded</MenuItem><MenuItem value="form_data">Form data</MenuItem>
              </TextField>
              <TextField select label="Encoding" value={apiForm.encoding || 'utf8'} onChange={(event) => setApiForm((current) => ({ ...current, encoding: event.target.value }))}>
                <MenuItem value="utf8">UTF-8</MenuItem><MenuItem value="ascii">ASCII</MenuItem><MenuItem value="base64">Base64</MenuItem>
              </TextField>
              <TextField type="number" label="Retry count" value={apiForm.retryCount ?? 1} disabled={!apiForm.retryOnFailure} inputProps={{ min: 0, max: 3 }} onChange={(event) => setApiForm((current) => ({ ...current, retryCount: Math.min(3, Math.max(0, Number(event.target.value))) }))} />
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControlLabel control={<Switch checked={Boolean(apiForm.retryOnFailure)} onChange={(event) => setApiForm((current) => ({ ...current, retryOnFailure: event.target.checked }))} />} label="Retry on 5xx failure" />
              <FormControlLabel control={<Switch checked={apiForm.followRedirects !== false} onChange={(event) => setApiForm((current) => ({ ...current, followRedirects: event.target.checked }))} />} label="Follow redirects" />
              <FormControlLabel control={<Switch checked={apiForm.strictSSL !== false} onChange={(event) => setApiForm((current) => ({ ...current, strictSSL: event.target.checked }))} />} label="Strict SSL" />
            </Stack>
            <TextField label="Default message on API failure" value={apiForm.defaultMessage || ''} onChange={(event) => setApiForm((current) => ({ ...current, defaultMessage: event.target.value }))} multiline minRows={2} fullWidth />
            <KeyValueEditor
              label="Headers"
              items={headerRows}
              onChange={setHeaderRows}
              valueLabel="Header value"
              helpText="Add request headers as simple key/value rows."
            />
            <KeyValueEditor
              label="Query params"
              items={queryRows}
              onChange={setQueryRows}
              valueLabel="Param value"
              helpText="These values will be appended to the URL query string."
            />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Use flow variables inside saved API payloads with placeholders like <code>{'{{{reqday}}}'}</code> or <code>{'{{customer_email}}'}</code>.
              </Typography>
              <JsonField
                label="Body"
                value={bodyText}
                onChange={(value) => {
                  setBodyText(value);
                  if (bodyError) {
                    setBodyError('');
                  }
                }}
                minRows={8}
                helperText={bodyError || (['raw', 'xml'].includes(apiForm.bodyType) ? 'Enter the raw request payload.' : 'Enter a JSON object. Variables like {{{reqday}}} are supported.')}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={saveApi} disabled={saving || !apiForm.name.trim() || !apiForm.url.trim()}>
            {saving ? 'Saving...' : apiForm.apiId ? 'Update API' : 'Save API'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
